<template>
  <div>
    <v-toolbar color="primary" dark tabs flat dense>
      <v-app-bar-nav-icon @click="$emit('toggle-drawer')"></v-app-bar-nav-icon>
      <v-tabs v-if="pages" v-model="tabRef" background-color="primary" grow show-arrows>
        <v-tabs-slider color="accent"></v-tabs-slider>
        <v-tab v-for="page in pages" :key="page.title">{{ page.title }}</v-tab>
      </v-tabs>
      <v-spacer></v-spacer>
      <v-menu offset-y>
        <template #activator="{ on: menuOn }">
          <v-tooltip top>
            <template #activator="{ on: tooltipOn }">
              <v-btn icon :disabled="loadPending" v-on="{ ...menuOn, ...tooltipOn }">
                <v-icon>mdi-download</v-icon>
              </v-btn>
            </template>
            <span>Download Outputs</span>
          </v-tooltip>
        </template>
        <v-list dense>
          <v-list-item v-for="item in downloadMenu" dense :key="item.text" @click="item.action">
            <v-list-item-content>
              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-list-item-action-text>{{ item.extension }}</v-list-item-action-text>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-toolbar>

    <v-expand-transition v-if="loadPending">
      <v-dialog :value="loadPending" hide-overlay persistent width="300">
        <v-card color="primary" dark>
          <v-card-text>
            Loading...
            <v-progress-linear indeterminate color="white"></v-progress-linear>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-expand-transition>
    <v-tabs-items v-if="pages" v-model="tabRef">
      <v-tab-item v-for="page in pages" :key="page.title">
        <component
          v-if="page.component"
          :is="page.component"
          v-bind="page.props"
          :ref="page.title === 'Power Flow' ? 'powerFlowDashboard' : undefined"
        />
        <CardSimple
          v-for="section in page.sections"
          :key="section.title"
          :title="section.title"
          v-bind="section.cardProps"
        >
          <v-tabs v-if="section.tabs">
            <template v-for="tab in section.tabs">
              <v-tab
                :key="tab.name"
                :href="'#' + $utils.kebabCase(page.title + section.title + tab.name)"
                >{{ tab.name }}</v-tab
              >
              <v-tab-item
                :key="tab.name"
                :value="$utils.kebabCase(page.title + section.title + tab.name)"
                lazy
              >
                <OutputSection :section="section"></OutputSection>
              </v-tab-item>
            </template>
          </v-tabs>
          <OutputSection v-else :section="section" :panel="section.panel"></OutputSection>
        </CardSimple>
      </v-tab-item>
    </v-tabs-items>
    <v-snackbar
      v-model="npcDowloadProgress.value"
      app
      bottom
      right
      text
      color="primary"
      :timeout="-1"
    >
      <div>{{ npcDowloadProgress.text }}</div>
    </v-snackbar>
    <NPCTimeseriesDownload
      v-if="isNPCSqlOutput"
      v-model="npcTimeseriesDialog"
      :current-case="currentCase"
      :parquet-files="npcParquetFiles"
      :yearly-output="npcOutput"
    >
    </NPCTimeseriesDownload>
  </div>
</template>
<script>
import chartUtils from "@/mixins/chartUtils";
import fieldUtils from "@/mixins/fieldUtils";
import outputs from "@/mixins/outputs";
import npcOutputsMixin from "@/mixins/outputs/npc";
import { camelToTitleCase, chartDownloadName, snakeToTitleCase, titleCase } from "@/plugins/utils";
import download from "downloadjs";
import Zip from "jszip";
import Papa from "papaparse";
import { mapActions, mapGetters, mapState } from "vuex";

export default {
  name: "CaseOutput",
  mixins: [fieldUtils, chartUtils, outputs, npcOutputsMixin],
  components: {
    CardSimple: () => import("@/components/CardSimple.vue"),
    OutputSection: () => import("@/components/OutputSection.vue"),
    NPCTimeseriesDownload: () => {
      return import("@/components/output/npc/NPCTimeseriesDownload.vue");
    },
  },
  props: {
    currentCase: Object,
    num_years: Number,
  },
  data() {
    return {
      type: null,
      tabRef: 0,
      job_id: null,
      generatorHeaders: [
        { text: "Name", value: "name" },
        { text: "Type", value: "type", parseEnum: true },
        { text: "Source", value: "source", parseEnum: true },
      ],
      storageHeaders: [
        { text: "Name", value: "name" },
        { text: "Source", value: "source", parseEnum: true },
      ],
      projectHeaders: [
        { text: "Name", value: "name" },
        { text: "Node ID", value: "PTID" },
        { text: "Power", value: "power" },
        { text: "Duration", value: "duration" },
      ],
      havePageBeenSetFromUrl: false,
      outputs: [],
    };
  },
  computed: {
    ...mapState({}),
    ...mapGetters({
      jobLoadPending: "jobs/loadPending",
      outputLoadPending: "outputs/loadPending",
      outputProfileLoadPending: "output-profiles/loadPending",
      getProfile: "profiles/get",
    }),
    activeTab() {
      return this.tabRef;
    },
    loadPending() {
      return (
        // this.jobLoadPending ||
        this.outputLoadPending ||
        this.outputProfileLoadPending ||
        this.pages == null ||
        this.pages.length === 0
      );
    },
    caseId() {
      return this.currentCase ? this.currentCase.id : null;
    },
    reserves() {
      return this.$utils.getIfExists(this.input, ["reserves"], []);
    },
    settings() {
      if (this.currentCase && this.currentCase.settings) {
        return this.currentCase.settings;
      }
      return null;
    },
    PFsettings() {
      if (this.currentCase && this.currentCase.settings.POWER_FLOW) {
        return this.currentCase.settings.POWER_FLOW;
      }
      return null;
    },
    input() {
      if (this.currentCase && this.currentCase.input) {
        return this.currentCase.input;
      }
      return null;
    },
    output() {
      return this.outputs.reduce((acc, curValue) => {
        this.job_id = curValue.id;
        this.type = curValue.type;
        const res = acc;
        let { output } = curValue;
        // handle historical outputs where the output_sql was in the DB
        // mid-historical cases where output_sql is filled from blob storage
        // and newer cases where output_sql is never filled.
        if (!output && (curValue.output_sql || curValue.output_url)) {
          if (curValue.type_name === "STACKED_SERVICES_EMULATOR") {
            output = [];
          } else if (curValue.type_name === "PRODUCTION_COST") {
            output = {};
          }
        }
        res[curValue.type_name] = output;
        return res;
      }, {});
    },
    pages() {
      if (!this.input || !this.output) return [];

      const pages = [];
      if (this.output.ALTERNATIVE_ANALYSIS) pages.push(this.AAOutput);

      if (this.output.PRODUCTION_COST && this.type && this.type === 10)
        pages.push(this.nodalPCOutput);

      if (this.output.PRODUCTION_COST && this.type && this.type === 2) pages.push(this.PCOutput);

      if (this.output.PRODUCTION_COST && this.output.ALTERNATIVE_ANALYSIS)
        pages.push(this.IRPDashboard);

      if (this.output.STACKED_SERVICES_EMULATOR) {
        pages.push(this.SSEOutput);
      }

      if (this.output.EFFECTIVE_LOAD_CARRYING_CAPABILITY) pages.push(this.ELCCOutput);

      if (this.output.POWER_FLOW) pages.push(this.PowerFlowOutput);

      if (this.output.SHORT_CIRCUIT) pages.push(this.SCOutput);

      if (this.output.TRANSIENT_STABILITY) pages.push(this.TStabilityOutput);

      if (this.output.LOAD_FORECAST) pages.push(this.LoadForecastOutput);

      const result = Object.values(pages);
      console.debug("page", result);
      return result;
    },
    isNPCSqlOutput() {
      const npcOutputs = this.outputs.find((v) => v.type_name === "PRODUCTION_COST");
      return npcOutputs && !npcOutputs.output && npcOutputs.output_url;
    },
    hasRegularOutputs() {
      // Has outputs without custom download handling?
      // this was always going to be broken since the type name of PC and NPC are the same
      const regularOutputs = this.outputs
        .filter((v) => v.type_name !== "PRODUCTION_COST" || !v.output_sql)
        .filter((v) => v.type_name !== "POWER_FLOW")
        .filter((v) => v.type_name !== "LOAD_FORECAST");
      return regularOutputs.length > 0;
    },
    npcParquetFiles() {
      return this.isNPCSqlOutput
        ? this.outputs.find((v) => v.type_name === "PRODUCTION_COST").parquet_files
        : null;
    },
    downloadMenu() {
      const downloadList = [];
      if (this.hasRegularOutputs) {
        downloadList.push({
          text: "Folder with CSV files",
          extension: ".zip",
          action: () => this.downloadOutputs(this.currentCase.id),
        });
      }
      if (this.output.POWER_FLOW) {
        downloadList.push({
          text: "Power Flow CSV files (within selected dates)",
          extension: ".zip",
          action: () => this.downloadPFCSV(),
        });
      }
      if (this.output.LOAD_FORECAST) {
        downloadList.push({
          text: "Folder with Load Forecast CSV files",
          extension: ".zip",
          action: () => this.downloadLoadForecastCSV(this.output.LOAD_FORECAST),
        });
      }
      if (this.isNPCSqlOutput) {
        downloadList.push({
          text: "NPC timeseries outputs...",
          extension: "",
          action: () => {
            this.npcTimeseriesDialog = true;
          },
        });
        downloadList.push({
          text: "NPC yearly outputs",
          extension: ".zip",
          action: () => this.npcSpreadsheetDownload(this.currentCase),
        });
        // NPC Parquet files are queried directly from blob storage
        // No need to download the entire database anymore
      }
      // SSE Parquet files are queried directly from blob storage
      // No need to download the entire database anymore
      return downloadList;
    },
  },
  watch: {
    pages() {
      if (
        !this.havePageBeenSetFromUrl &&
        this.$route.query &&
        this.$route.query.job &&
        this.pages.length
      ) {
        const pagesNames = {
          AA: "Alternative Analysis",
          PC: "Production Cost",
          SSE: "Stacked Services Emulator",
          ELCC: "ELCC",
          PF: "Power Flow",
          SC: "Short Circuit",
        };
        const pageIndex = this.pages.findIndex(
          (p) => p.title === pagesNames[this.$route.query.job]
        );
        this.tabRef = pageIndex;
        this.havePageBeenSetFromUrl = true;
      }
    },
    caseId() {
      this.retrieveOutputs();
    },
    isNPCSqlOutput() {
      this.parseNPCSqlOutputs();
    },
  },
  mounted() {
    if (this.caseId) {
      this.retrieveOutputs();
    }
  },
  methods: {
    ...mapActions({
      downloadOutputs: "cases/downloadOutputs",
      downloadSQL: "outputs/downloadSQL",
      loadOutputProfiles: "output-profiles/find",
      findProfile: "profiles/get",
      findJobs: "jobs/find",
      getOutput: "outputs/get",
    }),
    titleCase,
    snakeToTitleCase,
    camelToTitleCase,
    chartDownloadName,
    /**
     * Creates an array of objects
     * Each object has the identifying properties of the input, plus a 'value' property
     * The value property == outputObj[field], where the outputObj matches the input object
     */
    mergeObjectField(config, objects, field, headers = [{ value: "name" }]) {
      const defaultConfig = { inputId: "id", outputId: "id" };
      const { inputSource, outputSource, inputId, outputId } = {
        ...defaultConfig,
        ...config,
      };
      let objectTypes = this.$utils.deepClone(objects);
      if (!Array.isArray(objectTypes)) {
        objectTypes = [objectTypes];
      }
      // Allow multiple types in one call (generators, storage, etc.)
      return (
        objectTypes
          .map((type) => {
            const inputObjs = inputSource[type];
            const outputObjs = outputSource[type];
            if (inputObjs == null) {
              throw Error(`Invalid inputObjs at mergeObjectField. Field: ${field}`);
            }
            if (outputObjs == null) {
              throw Error(`Invalid outputObjs at mergeObjectField. Field: ${field}`);
            }

            return (
              outputObjs
                // Filters out non-thermal generators for thermal generator-only fields
                .filter((output) => output[field] != null)
                .map((output) => {
                  // inputObjs tend to have identifiers (name, type, source) that the outputObj needs
                  let input = inputObjs.find((i) => i[inputId] === output[outputId]);

                  // TODO: Remove the find failsafe. Dependent on proper id passthrough
                  if (input == null) {
                    input = inputObjs[output[outputId]];
                  }

                  // the table headers are typically passed, which don't include the object's ID

                  const result = {
                    id: input.id,
                  };
                  headers
                    // Sometimes an object won't have certain headers
                    // ex: demand resposnse doesn't have a type or source
                    // Sometimes an output will have the header, but not the input
                    // ex: Project outputs have a PTID
                    .filter((header) => input[header.value] != null || output[header.value] != null)
                    .forEach((header) => {
                      result[header.value] = input[header.value] || output[header.value];
                    });
                  result.value = output[field];

                  return result;
                })
            );
          })
          // Without this the first dimension of the array aligns with objects
          .flat()
      );
    },
    mapValue(source) {
      return (item) => ({ ...item, value: source[item.value] });
    },
    mapSumValues(el) {
      return {
        ...el,
        value: el.value.map((year) => {
          if (Array.isArray(year)) {
            return year.reduce((a, e) => a + e);
          }
          return year;
        }),
      };
    },
    retrieveOutputs() {
      this.findJobs({
        query: {
          case_id: this.caseId,
        },
      }).then((jobsResponse) => {
        const sqlJobTypes = [7, 10];
        const jobs = jobsResponse.data;
        jobs.forEach((job) => {
          if (!sqlJobTypes.includes(job.type)) {
            // Find json outputs
            this.getOutputQuery(job.id);
          } else if (sqlJobTypes.includes(job.type)) {
            // Find sql ouptuts
            this.getOutput([
              job.id,
              { query: { $select: ["id", "case_id", "output_sql", "output_url"] } },
            ]).then((outputResponse) => {
              // Check if there are sql outputs
              if (outputResponse.output_url) {
                // Store the URL directly - we'll query Parquet files via DuckDB
                this.outputs.push(outputResponse);
              } else if (!outputResponse.output_sql) {
                this.getOutputQuery(job.id);
              } else {
                this.outputs.push(outputResponse);
              }
            });
          }
        });
      });
    },
    getOutputQuery(id) {
      return this.getOutput([id, { query: { $select: ["id", "case_id", "output"] } }]).then(
        (outputResponse) => {
          this.outputs.push(outputResponse);
        }
      );
    },
    downloadSQL() {
      let file = this.outputs.find((v) => v.type_name === "STACKED_SERVICES_EMULATOR").output_sql;
      file = Buffer.from(file.data || file);
      const blob = new Blob([file], { type: "application/octet-stream" });
      download(blob, `${this.currentCase.name}_SSE.db`, "application/octet-stream");
    },
    dowloadDirectURL(url) {
      window.open(url);
    },
    downloadPFCSV() {
      const { powerFlowDashboard } = this.$refs;

      if (!powerFlowDashboard) {
        throw new Error("Power Flow Dashboard component not found. Cannot export CSV data.");
      }

      // Handle case where ref is an array (from v-for loop)
      const dashboardComponent = Array.isArray(powerFlowDashboard)
        ? powerFlowDashboard[0]
        : powerFlowDashboard;

      if (!dashboardComponent || typeof dashboardComponent.exportToCSV !== "function") {
        throw new Error(
          "Power Flow Dashboard exportToCSV method not found. Component may not be fully loaded."
        );
      }

      dashboardComponent.exportToCSV();
    },
    downloadLoadForecastCSV(loadForecastOutput) {
      const historicYears = () => {
        const startYear = this.currentCase.settings.year_start;
        const presentYear = this.currentCase.settings.LOAD_FORECAST.year_present;
        const dataPoints = [];
        for (let year = startYear; year < presentYear; year += 1) {
          dataPoints.push(year);
        }
        return dataPoints;
      };
      const forecastYears = () => {
        const presentYear = this.currentCase.settings.LOAD_FORECAST.year_present;
        const endYear = this.currentCase.settings.year_end;
        const dataPoints = [];
        for (let year = presentYear; year <= endYear; year += 1) {
          dataPoints.push(year);
        }
        return dataPoints;
      };

      const zip = new Zip();
      const historicData = historicYears();
      const forecastData = forecastYears();

      if (
        historicData.length !== loadForecastOutput.historicalAnnualLoads.length ||
        historicData.length !== loadForecastOutput.historicalFormulaPeakLoads.length
      ) {
        throw new Error("Historical data arrays have mismatched lengths");
      }

      if (
        forecastData.length !== loadForecastOutput.annualLoads.length ||
        forecastData.length !== loadForecastOutput.peakLoads.length
      ) {
        throw new Error("Forecast data arrays have mismatched lengths");
      }

      const historicRows = historicData.map((year, i) => ({
        Year: year,
        "Annual Energy Demand": loadForecastOutput.historicalAnnualLoads[i],
        "Annual Peak Demand": loadForecastOutput.historicalFormulaPeakLoads[i],
      }));

      const forecastRows = forecastData.map((year, i) => ({
        Year: year,
        "Annual Energy Demand": loadForecastOutput.annualLoads[i],
        "Annual Peak Demand": loadForecastOutput.peakLoads[i],
      }));

      const allRows = [...historicRows, ...forecastRows];

      zip.file("load_forecast.csv", Papa.unparse(allRows, { newline: "\n" }));

      const generalMetricsRows = this.generateGeneralMetricsCSV(loadForecastOutput);
      zip.file(
        "load_forecast_general_metrics.csv",
        Papa.unparse(generalMetricsRows, { newline: "\n" })
      );

      const featureMetricsRows = this.generateFeatureMetricsCSV(loadForecastOutput);
      zip.file(
        "load_forecast_feature_metrics.csv",
        Papa.unparse(featureMetricsRows, { newline: "\n" })
      );

      zip.generateAsync({ type: "blob" }).then((blob) => {
        download(blob, `${this.currentCase.name}.zip`);
      });
    },
    generateGeneralMetricsCSV(loadForecastOutput) {
      if (!loadForecastOutput.metrics || !loadForecastOutput.metrics_ridge) {
        return [{ Metric: "No metrics data available", Value: "" }];
      }
      const metrics = loadForecastOutput.metrics || {};
      const metricsRidge = loadForecastOutput.metrics_ridge || {};

      return [
        { Metric: "Multiple R", Value: metrics.multiple_r },
        { Metric: "R squared", Value: metrics.r_square },
        { Metric: "Adjusted R squared", Value: metrics.adjusted_r_square },
        {
          Metric: "Standard Error Regression (GWh)",
          Value: metrics.standard_error_regression,
        },
        { Metric: "Observations", Value: metrics.observations },
        { Metric: "F Statistic", Value: metrics.f_statistic },
        { Metric: "P-value of F Statistic", Value: metrics.p_value_f },
        { Metric: "Degrees of Freedom (Total)", Value: metrics.df_total },
        { Metric: "Degrees of Freedom (Regression)", Value: metrics.df_regression },
        { Metric: "Degrees of Freedom (Residual)", Value: metrics.df_residual },
        { Metric: "Sum of Squares (Total)", Value: metrics.ss_total },
        { Metric: "Sum of Squares (Regression)", Value: metrics.ss_regression },
        { Metric: "Sum of Squares (Residual)", Value: metrics.ss_residual },
        { Metric: "Mean Square (Regression)", Value: metrics.ms_regression },
        { Metric: "Mean Square (Residual)", Value: metrics.ms_residual },
        { Metric: "Intercept", Value: metricsRidge.intercept },
      ];
    },
    generateFeatureMetricsCSV(loadForecastOutput) {
      if (!loadForecastOutput.metrics_ridge) {
        return [{ Feature: "No feature metrics data available", Value: "" }];
      }
      const ridgeMetrics = loadForecastOutput.metrics_ridge || {};
      const featureNames = Object.keys(ridgeMetrics.coefficients || {});
      const ridgeMetricsRows = [];

      featureNames.forEach((feature) => {
        const featureName = this.formatFeatureName(feature);
        ridgeMetricsRows.push({
          Feature: featureName,
          Coefficient: ridgeMetrics.coefficients[feature],
          "Standard Error": ridgeMetrics.standard_errors[feature],
          "t-Statistic": ridgeMetrics.t_stats[feature],
          "P-value": ridgeMetrics.p_values_coefficients[feature],
          "Confidence Interval Lower": ridgeMetrics.confidence_intervals[feature][0],
          "Confidence Interval Upper": ridgeMetrics.confidence_intervals[feature][1],
        });
      });

      return ridgeMetricsRows;
    },
    formatFeatureName(feature) {
      const nameMap = {
        gdp: "GDP",
        population: "Population",
        evs: "Electric Vehicles",
        energy_intensity: "Energy Intensity",
      };
      return nameMap[feature] || feature;
    },
  },
};
</script>

<style></style>
